<template>
  <div class="storyblok-teaser-departments-scroll is-medium-wide" :style="`--amount-of-items: ${items.length};`">
    <UtilWrapperLayout>
      <UtilGrid class="block-teaser-news-scroll__grid">
        <div
          ref="scrollWrapperRef"
          class="storyblok-teaser-departments-scroll__wrapper"
        >
          <div
            class="storyblok-teaser-departments-scroll__inner-wrapper"
          >
            <div ref="scrollContentRef" class="storyblok-teaser-departments-scroll__inner-wrapper-inner">
              <div
                v-for="item in items"
                :key="item._uid"
                class="storyblok-teaser-departments-scroll__card"
              >
                <BlockItemCardScroll
                  :image="item.image"
                  :title="item.title"
                  :lead="item.lead"
                  :text="item.text"
                  :link="item.link"
                  :link-label="item.linkLabel"
                  :color="item.color"
                  :filter-query="item.filterQuery"
                  class="storyblok-teaser-departments-scroll__popup-card"
                />
              </div>
            </div>
          </div>
        </div>
      </UtilGrid>
    </UtilWrapperLayout>
  </div>
</template>

<script setup>
defineProps({
    items: {
        type: Array,
        required: true,
        default: () => [],
        validator: (value) => value.length > 0,
    },
});
const scrollWrapperRef = ref(null);
const scrollContentRef = ref(null);

const findCenteredCard = () => {
    if (!scrollContentRef.value || !scrollWrapperRef.value) return;

    const cards = scrollContentRef.value.querySelectorAll('.storyblok-teaser-departments-scroll__card');
    const viewportCenter = window.innerWidth / 2;
    const viewportQuarter = window.innerWidth / 4;

    let minDistance = Infinity;

    cards.forEach((card) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + (cardRect.width / 2);
        const distance = Math.abs(cardCenter - viewportCenter);

        const maxDistance = window.innerWidth / 2;
        const scale = 1 - (1 / (viewportCenter / distance));
        const translateZ = Math.round((distance / maxDistance) * -300);
        const translateX = 0 - (100 / (viewportCenter * distance));
        const opacity = Math.max(
            0,
            Math.min(1, 1 - (distance - viewportQuarter) / (viewportQuarter / 2)),
        );

        requestAnimationFrame(() => {
            card.style.setProperty('transform', `scale(${scale}) translateZ(${translateZ}px) translateX(${translateX}%)`);
            card.style.setProperty('opacity', opacity);
        });

        if (distance < minDistance) {
            minDistance = distance;
        }
    });

    requestAnimationFrame(findCenteredCard);
};

let raf = null;
const clientDeviceStore = useClientDeviceStore();

onMounted(() => {
    nextTick(() => {
        // Add a small delay to ensure content is fully rendered
        const { createScrollTrigger } = useScrollTrigger();
        if (scrollContentRef.value && scrollWrapperRef.value) {
            // Calculate dimensions once
            const contentWidth = scrollContentRef.value.scrollWidth;
            const wrapperWidth = scrollWrapperRef.value.offsetWidth;
            const scrollWidth = contentWidth - wrapperWidth;

            // Get the current padding and margin values
            const computedStyle = window.getComputedStyle(scrollContentRef.value);
            const paddingLeft = parseFloat(computedStyle.paddingLeft);
            const cardStyle = window.getComputedStyle(scrollContentRef.value.querySelector('.storyblok-teaser-departments-scroll__card'));
            const cardMarginRight = parseFloat(cardStyle.marginRight);

            // Adjust scroll width to account for padding and margins
            const adjustedScrollWidth = scrollWidth + paddingLeft + Math.abs(cardMarginRight);

            // Set initial position based on direction
            createScrollTrigger({
                trigger: scrollWrapperRef.value,
                start: 'center bottom',
                end: () => `+=${window.innerHeight * 1}`,
                onUpdate: (self) => {
                    if (scrollContentRef.value) {
                        const { progress } = self;
                        // Add mobile speed multiplier
                        const speedMultiplier = clientDeviceStore.isMobile
                            ? 0.8 : 0.9; // Slower on mobile
                        const translateX = (-progress * adjustedScrollWidth * speedMultiplier);
                        scrollContentRef.value.style.transform = `translateX(${translateX}px)`;
                    }
                },
            });
        }
    });
    raf = requestAnimationFrame(findCenteredCard);
});

onUnmounted(() => {
    cancelAnimationFrame(raf);
});

</script>

<style lang="scss" scoped>
.storyblok-teaser-departments-scroll {
    @include fluid('margin-bottom', 50px, 80px);
    position: relative;
    // overflow: hidden;
}

.storyblok-teaser-departments-scroll__wrapper {
    @include default-content-columns;
    position: relative;
    width: 100%;
    max-width: 100%;
    height: calc(var(--amount-of-items) * 50vh);
}

.storyblok-teaser-departments-scroll__inner-wrapper {
    position: sticky;
    top: 25vh;
    display: flex;
    // overflow: hidden;
    width: 100%;
    flex-direction: row;
    padding-bottom: 20px;
    gap: 0;
    transform-style: preserve-3d;
    will-change: transform;

    @include mobile {
        top: 10vh;
        overflow: hidden;
    }
}

.storyblok-teaser-departments-scroll__inner-wrapper-inner {
    position: relative;
    display: flex;
    width: 100%;
    flex-direction: row;
    padding-bottom: 20px;
    gap: 0;
    transform-style: preserve-3d;
    will-change: transform;
}

.storyblok-teaser-departments-scroll__card {
    position: relative;
    flex-shrink: 0;
    margin-right: -350px; // Increased overlap
    transform-origin: center center;
    transform-style: preserve-3d;
    will-change: transform;

    @include tablet {
        margin-right: -250px;
    }

    @include tablet-portrait {
        flex-basis: 300px;
        margin-right: -200px;
    }
}

.storyblok-teaser-departments-scroll__image {
    height: 320px;
}

.storyblok-teaser-departments-scroll__heading {
    padding: 40px 40px 67px 40px;
    color: var(--c-petrol-1);
}

.storyblok-teaser-departments-scroll__title {
    font-family: var(--f-family--black);
    font-size: var(--f-size--h3);
    line-height: var(--l-height--h3);
}

.storyblok-teaser-departments-scroll__lead {
    max-width: 345px;
    font-family: var(--f-family--regular);
    font-size: var(--f-size--h4);
    line-height: var(--l-height--h4);
}

.storyblok-teaser-departments-scroll__mobile-cards {
    @include fluid('margin-bottom', 70px, 80px);

    display: none;
    flex-wrap: wrap;
    gap: 20px;

    @include tablet {
        display: flex;
    }
}
</style>
